import * as React from 'react'

import { animate, useMotionValue, useTransform } from 'motion/react'
import * as m from 'motion/react-m'

import { EmptyState, Flex, Text } from '@buffer-mono/popcorn'

import { CommentsEngagementPanel } from '../CommentsEngagementPanel'
import type { EmptyStateChannel } from './types'
import { EmptyStateWrapper } from '../../../components/EmptyStates'
import { isGoogleBusinessProfile } from '../../../util/channelHelpers'

import styles from './RepliedToManyComments.module.css'

import type { JSX } from 'react'

const EmptyStateIconMotion = m.create(EmptyState.Icon)

type RepliedToManyCommentsProps = {
  score: number
  shouldAnimate?: boolean
  channels?: EmptyStateChannel[]
  postId?: string
}

export const RepliedToManyComments = ({
  score,
  shouldAnimate,
  channels,
  postId,
}: RepliedToManyCommentsProps): JSX.Element => {
  const isGBP = isGoogleBusinessProfile(channels)
  const itemLabel = isGBP ? 'reviews' : 'comments'
  const progress = useMotionValue(0)
  const roundedProgress = useTransform(() => Math.round(progress.get()))
  const filter = useTransform(progress, [0, score], ['blur(10px)', 'blur(0px)'])
  const scale = useTransform(progress, [0, score], [0.5, 1])
  const opacity = useTransform(progress, [0, score], [0.5, 1])

  const maxDuration = 2 // seconds for 100
  const minDuration = 0.5 // minimum duration for very low values
  const duration = Math.max((score / 100) * maxDuration, minDuration)

  React.useEffect(() => {
    animate(progress, score, {
      duration,
      ease: [0.31, 0.05, 0.28, 0.85],
    })
  }, [progress, duration, score])

  return (
    <EmptyStateWrapper shouldAnimate={shouldAnimate}>
      <EmptyState
        size="large"
        variant="success"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- EmptyState is the labelled live status while retaining its Popcorn presentation.
        role="status"
        aria-label={`All ${itemLabel} have been addressed`}
        className={styles.wrapper}
      >
        <Flex gap="xs" direction="column" align="center">
          <div className={styles.animationContainer} aria-hidden="true">
            <EmptyStateIconMotion
              className={styles.emptyStateIcon}
              animate={{
                scale: [1, 1.2, 1],
                transition: {
                  duration: 1,
                },
                backgroundColor: [
                  'var(--color-bg-success-subtle)',
                  'var(--global-color-green-300)',
                  'var(--color-bg-success-subtle)',
                ],
              }}
            />
            <m.div
              className={styles.scoreText}
              style={{
                filter,
                scale,
                opacity,
                willChange: 'filter, transform, opacity',
              }}
            >
              {roundedProgress}
            </m.div>
          </div>
          <Text size="md" color="subtle" weight="bold">
            {isGBP ? 'Reviews' : 'Comments'} replied
          </Text>
        </Flex>
        <EmptyState.Heading>
          Great job replying to so many {itemLabel}! 💅 You&apos;re creating the
          momentum for your community.
        </EmptyState.Heading>
        <EmptyState.Description>
          You&apos;re all caught up! We will let you know when new {itemLabel}{' '}
          appear.
        </EmptyState.Description>
      </EmptyState>
      <CommentsEngagementPanel channels={channels ?? []} postId={postId} />
    </EmptyStateWrapper>
  )
}
